/**
 * Lead Generation Bundles — Start / Grow / Scale / Performance.
 * Follows §5 of `LEAD_GENERATION_ROADMAP.md`. Each bundle maps to a
 * concrete set of WebsiteTech services and a lead-generation outcome.
 *
 * Note: pricing is intentionally shown as "Starting from" with a Request
 * Proposal CTA because final pricing varies by country / market (§5).
 */
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Target, Rocket, TrendingUp, Crown, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";

const BUNDLES = [
  {
    key: "start",
    name: "START",
    tagline: "Get the acquisition fundamentals working.",
    Icon: Target,
    accent: "border-slate-200 bg-white",
    priceLabel: "Starting from Request Proposal",
    best: "Small businesses launching their first serious lead-gen engine.",
    includes: [
      { label: "Website optimization",   to: "/improve/modernization" },
      { label: "Basic SEO",              to: "/lead-generation/seo" },
      { label: "Analytics setup",        to: "/lead-generation/analytics" },
      { label: "AI Lead Capture layer",  to: "/lead-generation/lead-capture" },
    ],
    outcome: "A website that is measurable, searchable, and captures every visitor who's interested.",
  },
  {
    key: "grow",
    name: "GROW",
    tagline: "Add nurturing, local presence and reviews.",
    Icon: Rocket,
    accent: "border-brand/30 bg-blue-50/40 ring-2 ring-brand/10",
    recommended: true,
    priceLabel: "Starting from Request Proposal",
    best: "Businesses with existing demand that need to convert more of it.",
    includes: [
      { label: "Everything in START" },
      { label: "Local SEO",                   to: "/lead-generation/seo" },
      { label: "AI Chatbot & Assistants",     to: "/ai/assistants" },
      { label: "CRM integration",             to: "/lead-generation/crm" },
      { label: "Lead Nurturing automation",   to: "/lead-generation/nurturing" },
      { label: "Review & Reputation",         to: "/lead-generation/reputation" },
    ],
    outcome: "A lifecycle engine: more local visibility, warmer leads, faster follow-up, better reputation.",
  },
  {
    key: "scale",
    name: "SCALE",
    tagline: "Add paid acquisition, qualification and attribution.",
    Icon: TrendingUp,
    accent: "border-slate-200 bg-white",
    priceLabel: "Starting from Request Proposal",
    best: "Teams ready to invest in paid demand and need to prove ROI.",
    includes: [
      { label: "Everything in GROW" },
      { label: "Paid Advertising",             to: "/lead-generation/paid-advertising" },
      { label: "Advanced SEO",                 to: "/lead-generation/seo" },
      { label: "AI Lead Qualification",        to: "/lead-generation/lead-qualification" },
      { label: "Appointment Automation",       to: "/lead-generation/appointments" },
      { label: "Lead Attribution & ROI",       to: "/lead-generation/analytics" },
      { label: "Conversion Rate Optimization", to: "/lead-generation/conversion-optimization" },
    ],
    outcome: "Paid + organic running together with clear attribution. You know what's working — and what to double down on.",
  },
  {
    key: "performance",
    name: "PERFORMANCE",
    tagline: "Full-stack lead engine managed end-to-end.",
    Icon: Crown,
    accent: "border-amber-200 bg-amber-50/30",
    priceLabel: "Enterprise / Custom",
    best: "High-growth teams who want a managed acquisition engine with dedicated strategy.",
    includes: [
      { label: "Everything in SCALE" },
      { label: "Custom AI Agents",              to: "/ai/assistants" },
      { label: "Outbound B2B Lead Gen",         to: "/lead-generation/outbound" },
      { label: "Advanced CRM Automation",       to: "/lead-generation/crm" },
      { label: "Marketing Analytics",           to: "/lead-generation/analytics" },
      { label: "Multi-channel Campaigns",       to: "/visual-studio" },
      { label: "Dedicated Growth Strategy",     to: "/growth-operator" },
    ],
    outcome: "A dedicated growth operator plus the full WebsiteTech platform — a fully-managed acquisition engine.",
  },
];

export default function LeadGenBundles() {
  const [searchParams] = useSearchParams();
  const highlight = (searchParams.get("highlight") || "").toLowerCase();

  usePageSeo({
    title: "Lead Generation Bundles — Start / Grow / Scale / Performance | WebsiteTech.pro",
    description: "Four fixed-scope lead-generation bundles that bolt onto any WebsiteTech site. Pick a bundle, we assemble the right mix of SEO, paid, lead capture, CRM, nurturing and attribution — managed end to end.",
  });
  useBreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Lead Generation", url: "/lead-generation" },
    { name: "Bundles", url: "/lead-generation/bundles" },
  ]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="pt-28 pb-20" data-testid="leadgen-bundles-page">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand">
            Lead Generation Bundles
          </p>
          <h1 className="mt-2 font-heading font-extrabold text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Pick a bundle.<br />
            <span className="text-brand">We assemble the engine.</span>
          </h1>
          <p className="mt-4 text-base text-slate-600 max-w-3xl">
            Each bundle maps to a stage of the acquisition lifecycle — Attract, Convert,
            Automate, Measure, Grow. Start simple and upgrade when you need more horsepower.
            Final pricing varies by country and scope; request a proposal and we'll size it
            to your market.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {BUNDLES.map((b) => (
              <article
                key={b.key}
                data-testid={`bundle-card-${b.key}`}
                className={`rounded-2xl border p-5 flex flex-col ${b.accent} ${highlight === b.key ? "ring-4 ring-emerald-400/50 scale-[1.02] shadow-xl" : ""} transition-all`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-brand text-white flex items-center justify-center">
                    <b.Icon size={18} />
                  </div>
                  {b.recommended && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-brand text-white rounded-full px-2 py-1 flex items-center gap-1">
                      <Sparkles size={10} /> Most popular
                    </span>
                  )}
                </div>
                <h2 className="mt-4 font-heading font-extrabold text-slate-900 text-2xl">{b.name}</h2>
                <p className="mt-1 text-sm text-slate-600">{b.tagline}</p>
                <p className="mt-4 text-[11px] uppercase tracking-wider font-bold text-slate-400">Includes</p>
                <ul className="mt-2 space-y-1.5 flex-1">
                  {b.includes.map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                      {it.to ? (
                        <Link to={it.to} className="hover:text-brand hover:underline">{it.label}</Link>
                      ) : (
                        <span>{it.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-slate-200">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400">Best for</p>
                  <p className="mt-1 text-xs text-slate-600 leading-snug">{b.best}</p>
                </div>
                <div className="mt-3">
                  <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400">Outcome</p>
                  <p className="mt-1 text-xs text-slate-700 leading-snug">{b.outcome}</p>
                </div>
                <div className="mt-5">
                  <p className="text-[11px] text-slate-400">{b.priceLabel}</p>
                  <Link
                    to="/contact?topic=lead-generation-bundle"
                    data-testid={`bundle-cta-${b.key}`}
                    className="mt-2 btn-primary rounded-lg w-full py-2.5 text-sm font-semibold flex items-center justify-center gap-2"
                  >
                    Request Proposal <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <section className="mt-16 bg-white border border-slate-200 rounded-2xl p-6 lg:p-10" data-testid="bundles-cross-sell">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand">Not sure which bundle?</p>
            <h2 className="mt-2 font-heading font-extrabold text-slate-900 text-2xl sm:text-3xl">
              Run the free Lead Generation Audit — we'll recommend the right fit.
            </h2>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Enter your site URL. We score Visibility, Website, Conversion, Trust, Follow-Up,
              Automation and Analytics (each 0-100) and return a composite Lead Generation Score
              with prioritized next steps.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                to="/lead-generation/audit"
                data-testid="bundles-audit-cta"
                className="btn-primary rounded-lg px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
              >
                Run My Lead Generation Audit <ArrowRight size={14} />
              </Link>
              <Link
                to="/lead-generation"
                data-testid="bundles-overview-cta"
                className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-brand hover:text-brand"
              >
                Explore all 20 services
              </Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
